/* eslint-disable react/require-default-props */
import React, { useEffect, useCallback, useRef, useState } from 'react';

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import classnames from 'classnames/bind';
import {
    Image,
    Paperclip,
    CheckCircle2,
    Eye,
} from 'lucide-react';

import { notify } from '@NOTIFICATION/Notificator';

import {
    AdminSingleChapterData,
    AdminSingleChapterMeta,
} from '@INTERFACES/admin-api/chapters';

import { useUpdateChapter, attachmentsAdminApi } from '@SERVICES';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useAttachmentManagementModal from '@HOOKS/store/modals/useAttachmentManagementModal';

import ChapterName from '@PAGES/admin/edit-chapter/components/ChapterName';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import styles from './PageHeader.module.scss';

const cx: CX = classnames.bind(styles);

function PageHeader({
    chapterData, chapterMeta, actionButton, isNoIntroduction = false,
    setNoIntroduction = undefined,
}: Props) {
    const [updateChapter, {
        isSuccess, isError, error,
    }] = useUpdateChapter();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showImagePreview, setShowImagePreview] = useState(false);

    const {
        canEditDocument,
    } = useDocumentPermissions({
        documentOwnerId: chapterData.document.owner,
        permissions: chapterMeta.permissions,
    });

    const {
        actions: attachmentManagementModalActions,
    } = useAttachmentManagementModal(() => true);

    const { data: attachmentsData } = attachmentsAdminApi.useGetAttachmentsQuery({
        content_type: 'chapter',
        object_id: chapterData.id,
    });
    const attachments = attachmentsData || [];

    useEffect(() => {
        if (isSuccess) {
            notify.info('Chapter has been updated', {
                toastId: 'update-chapter-params',
            });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'update-chapter-params-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'update-chapter-params-error',
                });
            }
        }
    }, [isError, error]);

    const handleImageUpload = useCallback((file: File | null) => {
        if (file) {
            updateChapter({
                id: chapterData.id,
                documentId: chapterData.document.id,
                name: chapterData.name,
                body: chapterData.introductionText.body,
                isMandatory: chapterData.isMandatory,
                image: file,
            });
        }
    }, [chapterData, updateChapter]);

    const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files && files.length > 0) {
            handleImageUpload(files[0]);
        }
        // Reset the input value so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [handleImageUpload]);

    const handleImageCardClick = useCallback(() => {
        if (canEditDocument && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [canEditDocument]);

    const handleImagePreview = useCallback((e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering the upload click
        setShowImagePreview(true);
    }, []);

    const handleRemoveImage = useCallback(() => {
        updateChapter({
            id: chapterData.id,
            documentId: chapterData.document.id,
            name: chapterData.name,
            body: chapterData.introductionText.body,
            isMandatory: chapterData.isMandatory,
            image: null,
        });
    }, [chapterData, updateChapter]);

    return (
        <div className={cx('page-header')}>
            {/* Image Preview Modal */}
            {showImagePreview && chapterData.image && (
                <div className={cx('image-preview-modal')} onClick={() => setShowImagePreview(false)} role="button" tabIndex={0}>
                    <div className={cx('image-preview-content')} onClick={(e) => e.stopPropagation()} role="button" tabIndex={0}>
                        <div className={cx('image-preview-header')}>
                            <h3>Chapter Cover Image</h3>
                            <button 
                                className={cx('close-preview')}
                                onClick={() => setShowImagePreview(false)}
                                type="button"
                            >
                                ×
                            </button>
                        </div>
                        <div className={cx('image-preview-body')}>
                            <img src={chapterData.image} alt="Chapter cover" />
                        </div>
                        <div className={cx('image-preview-actions')}>
                            <BasicButton
                                title="Change Image"
                                variant="primary"
                                size="small"
                                onClick={() => {
                                    setShowImagePreview(false);
                                    handleImageCardClick();
                                }}
                            />
                            <BasicButton
                                title="Remove Image"
                                variant="danger"
                                size="small"
                                onClick={() => {
                                    setShowImagePreview(false);
                                    handleRemoveImage();
                                }}
                            />
                            <BasicButton
                                title="Close"
                                variant="neutral"
                                size="small"
                                onClick={() => setShowImagePreview(false)}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className={cx('page-wrap')}>
                {/* Main header section with chapter name and primary actions */}
                <div className={cx('header-main')}>
                    <div className={cx('chapter-info')}>
                        <ChapterName
                            chapterData={chapterData}
                            chapterMeta={chapterMeta}
                            actionButton={actionButton}
                            bodyContent={null}
                            isNoIntroduction={isNoIntroduction}
                            setNoIntroduction={setNoIntroduction}
                        />
                    </div>
                </div>

                {/* Options section with clickable cards */}
                <div className={cx('options-section')}>
                    <div className={cx('options-header')}>
                        <h3 className={cx('options-title')}>Chapter Options</h3>
                        <div className={cx('options-divider')} />
                    </div>
                    
                    <div className={cx('options-grid')}>
                        {/* Upload Cover Image Option Card */}
                        {canEditDocument && (
                            <div 
                                className={cx('option-card', { 'has-image': !!chapterData.image })}
                                onClick={handleImageCardClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleImageCardClick();
                                    }
                                }}
                            >
                                <div className={cx('option-content')}>
                                    <div className={cx('option-icon')}>
                                        <Image size={20} />
                                    </div>
                                    <div className={cx('option-info')}>
                                        <div className={cx('option-title')}>
                                            {chapterData.image ? 'Update Chapter Cover Image' : 'Upload Chapter Cover Image'}
                                        </div>
                                        <div className={cx('option-description')}>
                                            {chapterData.image ? 'Click to change the cover image for this chapter' : 'Click to set a cover image for this chapter'}
                                        </div>
                                    </div>
                                    <div className={cx('option-actions')}>
                                        {chapterData.image && (
                                            <button
                                                className={cx('preview-button')}
                                                onClick={handleImagePreview}
                                                type="button"
                                                title="View current image"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        )}
                                        <div className={cx('option-status')}>
                                            {chapterData.image && <CheckCircle2 size={18} />}
                                        </div>
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileInputChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        )}

                        {/* Manage Attachments Option Card */}
                        {canEditDocument && (
                            <div 
                                className={cx('option-card')}
                                onClick={() => {
                                    attachmentManagementModalActions.open({
                                        parentType: 'chapter',
                                        parentId: chapterData.id,
                                        parentName: chapterData.name,
                                        canEdit: canEditDocument,
                                    });
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        attachmentManagementModalActions.open({
                                            parentType: 'chapter',
                                            parentId: chapterData.id,
                                            parentName: chapterData.name,
                                            canEdit: canEditDocument,
                                        });
                                    }
                                }}
                            >
                                <div className={cx('option-content')}>
                                    <div className={cx('option-icon')}>
                                        <Paperclip size={20} />
                                    </div>
                                    <div className={cx('option-info')}>
                                        <div className={cx('option-title')}>
                                            Manage Attachments
                                            {attachments.length > 0 && (
                                                <span className={cx('attachment-count')}>
                                                    {' '}({attachments.length})
                                                </span>
                                            )}
                                        </div>
                                        <div className={cx('option-description')}>
                                            Upload and manage PDF attachments for this chapter
                                        </div>
                                    </div>
                                    <div className={cx('option-status')}>
                                        {attachments.length > 0 && <CheckCircle2 size={18} />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

PageHeader.defaultProps = {
    actionButton: null,
};

type Props = {
    chapterData: AdminSingleChapterData;
    chapterMeta: AdminSingleChapterMeta;
    actionButton?: React.ReactNode;
    isNoIntroduction?: boolean,
    setNoIntroduction?: (value: boolean) => void,
};

export default PageHeader;
